import { useRef, useState, useCallback, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classnames from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'
import PropTypes from 'prop-types'

// Selectors
import {
  selectHomeUpdateLoanBalanceLoading,
  selectHomeUpdateLoanBalanceSaved,
  selectHomeUpdateLoanBalanceRequested,
} from '../../../../store/selectors/home'

// Utils
import usePrev from '../../../../hooks/usePrev'
import { useToggle } from '../../../../hooks/useToggle'
import history from '../../../../history'

// Components
import { Button, If, Text, Unless } from '../../..'
import ShowPaidExtraPanel from './ShowPaidExtraPanel/ShowPaidExtraPanel'
import OverlayPanel from '../../Routing/OverlayPanel'
import ShowInfoPanel from './ShowInfo/ShowInfoPanel'
import ShowPaidExtraSuccess from './ShowPaidExtraSuccess/ShowPaidExtraSuccess'
import ShowPaidExtraConfirmPanel from './ShowPaidExtraConfirmPanel/ShowPaidExtraConfirmPanel'
import ShowNoChangeConfirmPanel from './ShowNoChangeConfirmPanel/ShowNoChangeConfirmPanel'

// Constants
import {
  LearnMore,
  InterestPayMoreMonthly,
} from '../../../../constants/trackingEvents'
import { OVERLAY_HELPERS } from '../../../../constants/navigation'
import { resetLoanBalancedSaved } from '../../../../actions/home.js'

// Styles
import styles from './Info.module.scss'

const propTypes = {
  withCurrentHomeData: PropTypes.bool,
  loanDetails: PropTypes.array,
  balanceDiff: PropTypes.number,
  hasSavedUPB: PropTypes.bool,
  upbRequested: PropTypes.bool,
}

const Info = ({
  withCurrentHomeData,
  loanDetails,
  balanceDiff,
  hasSavedUPB,
  upbRequested,
}) => {
  const dispatch = useDispatch()

  const { reset: resetShowInfo } = useToggle()

  const learnMoreRef = useRef(null)
  const overlayPanelRef = useRef(null)

  const saved = useSelector(selectHomeUpdateLoanBalanceSaved)
  const loading = useSelector(selectHomeUpdateLoanBalanceLoading)
  const requested = useSelector(selectHomeUpdateLoanBalanceRequested)

  const [submitted, setSubmitted] = useState(hasSavedUPB)
  const [loanChanged, setLoanChanged] = useState(false)
  const [explode, setExplode] = useState(false)

  const prevBalanceDiff = usePrev(balanceDiff)

  useEffect(() => {
    if (typeof prevBalanceDiff === 'number') {
      setExplode(balanceDiff > prevBalanceDiff)
    }
  }, [prevBalanceDiff, balanceDiff])

  const onSubmit = changed => {
    if (changed) {
      setSubmitted(true)
      setLoanChanged(true)
    } else {
      handleClickBack()
    }
  }

  const editPaidAmount = () => {
    setSubmitted(hasSavedUPB)
    handleAlreadyPaidExtraClick()
  }

  const handleLearnMoreClick = () => {
    history.push(OVERLAY_HELPERS.principalPaymentLearnMore.buildPath())
  }

  const handleAlreadyPaidExtraClick = () => {
    resetShowInfo()
    history.push(OVERLAY_HELPERS.unpaidBalance.buildPath())
  }

  const handleClickBack = () => {
    if (overlayPanelRef.current) {
      overlayPanelRef.current.back()
    }
    dispatch(resetLoanBalancedSaved())
  }

  const handleNavBack = useCallback(() => {
    setLoanChanged(false)
    dispatch(resetLoanBalancedSaved())
  }, [dispatch])

  const showSuccessBox = submitted && (balanceDiff > 0 || upbRequested)

  const intl = useIntl()

  return (
    <If condition={withCurrentHomeData}>
      <Text
        data-qa='info-section-learn-more-button'
        align='center'
        className={classnames(
          showSuccessBox && styles.hideButtons,
          styles.infoButtonWrapper
        )}
      >
        <Button
          data-qa='info.learnMoreButton'
          track={LearnMore}
          trackEnabled={withCurrentHomeData}
          trackOptions={{ eventLabel: InterestPayMoreMonthly }}
          onClick={handleLearnMoreClick}
        >
          <FormattedMessage
            id='Homeowner.interest.info.toggle'
            defaultMessage='Learn more about this'
          />
        </Button>
        <Button onClick={handleAlreadyPaidExtraClick}>
          <FormattedMessage
            id='Homeowner.interest.loanBalance.toggle'
            defaultMessage='Already paid extra?'
          />
        </Button>
      </Text>

      <If condition={showSuccessBox}>
        <ShowPaidExtraSuccess
          upbRequested={upbRequested}
          editPaidAmount={editPaidAmount}
          balanceDiff={balanceDiff}
        />
      </If>

      <OverlayPanel
        ref={learnMoreRef}
        back={OVERLAY_HELPERS.principalPaymentLearnMore.getOrigin()}
        path={OVERLAY_HELPERS.principalPaymentLearnMore.buildPath()}
        title={OVERLAY_HELPERS.getTitle('principalPayment', intl)}
        render={() => (
          <ShowInfoPanel
            intl={intl}
            withCurrentHomeData={withCurrentHomeData}
          />
        )}
      />

      <OverlayPanel
        ref={overlayPanelRef}
        back={OVERLAY_HELPERS.unpaidBalance.getOrigin()}
        path={OVERLAY_HELPERS.unpaidBalance.buildPath()}
        title={OVERLAY_HELPERS.getTitle('unpaidBalance', intl)}
        onClose={handleNavBack}
        render={() => (
          <>
            <Unless condition={requested}>
              <If condition={saved && loanChanged && explode}>
                <ShowPaidExtraConfirmPanel
                  onClickBack={handleClickBack}
                  saved={saved}
                />
              </If>
              <If condition={saved && !explode}>
                <ShowNoChangeConfirmPanel handleClickBack={handleClickBack} />
              </If>
            </Unless>
            <Unless condition={saved}>
              <ShowPaidExtraPanel
                loanDetails={loanDetails}
                loading={loading}
                onSubmit={onSubmit}
              />
            </Unless>
          </>
        )}
      />
    </If>
  )
}

Info.propTypes = propTypes

export default Info
